'use client';
import { MotionMaybe, getVariantFromSimpleControls } from '@/utils/animation';
import { getCTA } from '@/utils/getCTA';
import { getColorScheme } from '@/utils/getColorScheme';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { parseImagePosition } from '@/utils/parseImagePosition';
import {
    getReviewSourceAlt,
    normalizeReviewSourceIcon,
} from '@/utils/reviewSourceAlt';
import Carousel from 'nuka-carousel';
import { useEffect, useMemo, useRef, useState } from 'react';
import NiceJobStories from './NiceJobStories';
import { MarkDown } from './atoms/MarkDown';

const PACK_TOKENS = {
    classic: [],
    duotone: ['fa-duotone'],
    sharp: ['fa-sharp'],
    'sharp-duotone': ['fa-sharp-duotone', 'fa-sharp', 'fa-duotone'],
};

const normalizeStyleForPack = (style, pack) => {
    if (pack === 'brands') return 'brands';
    const allowed = ['solid', 'regular', 'light', 'thin'];
    return allowed.includes(style) ? style : 'solid';
};

const buildIconClass = (name, pack, style) => {
    const tokens = [];
    const normalizedPack = pack || 'classic';
    const normalizedStyle = normalizeStyleForPack(style, normalizedPack);
    (PACK_TOKENS[normalizedPack] || []).forEach((t) => tokens.push(t));
    if (normalizedPack !== 'brands') tokens.push(`fa-${normalizedStyle}`);
    tokens.push(`fa-${name}`);
    const seen = new Set();
    return tokens
        .filter((t) => {
            if (!t || seen.has(t)) return false;
            seen.add(t);
            return true;
        })
        .join(' ');
};

const FEATURE_ICON_SIZE_CLASSES = {
    default: '',
    'x-small': 'text-xl sm:text-2xl',
    small: 'text-2xl sm:text-3xl',
    medium: 'text-3xl sm:text-4xl',
    large: 'text-4xl sm:text-5xl',
    'x-large': 'text-5xl sm:text-6xl',
};

const getFeatureIconSizeClass = (value) =>
    FEATURE_ICON_SIZE_CLASSES[
        String(value || 'default')
            .toLowerCase()
            .trim()
    ] || '';

const SQUARE_CARD_SIZE_CLASSES = {
    default: 'h-[clamp(240px,70vw,280px)] w-[clamp(240px,70vw,280px)]',
    'x-small': 'h-[clamp(240px,70vw,280px)] w-[clamp(240px,70vw,280px)]',
    small: 'h-[clamp(250px,70vw,290px)] w-[clamp(250px,70vw,290px)]',
    medium: 'h-[clamp(260px,70vw,300px)] w-[clamp(260px,70vw,300px)]',
    large: 'h-[clamp(270px,72vw,315px)] w-[clamp(270px,72vw,315px)]',
    'x-large': 'h-[clamp(280px,72vw,330px)] w-[clamp(280px,72vw,330px)]',
};

const getSquareCardSizeClass = (value) =>
    SQUARE_CARD_SIZE_CLASSES[
        String(value || 'default')
            .toLowerCase()
            .trim()
    ] || SQUARE_CARD_SIZE_CLASSES.default;

const valOf = (value, fallback = '') => {
    if (value == null) return fallback;
    if (typeof value === 'object' && value && 'value' in value) {
        return value.value ?? fallback;
    }
    return value;
};

const hasText = (value) => {
    const normalized = valOf(value, '');
    if (value == null) return false;
    return (
        String(normalized)
            .replace(/<[^>]*>/g, '')
            .trim().length > 0
    );
};

const normalizeAnimation = (raw) => {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'none',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'none';
};

const normalizeAnimationGroup = (raw) => {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (
        ['ctas', 'ctas-only', 'cta', 'cta-only', 'buttons', 'button'].includes(
            v
        )
    ) {
        return 'ctas';
    }
    if (
        [
            'features',
            'feature',
            'reviews',
            'review',
            'cards',
            'cards-only',
        ].includes(v)
    ) {
        return 'features';
    }
    return 'content';
};

const useSiteIconOverride = () => {
    const [pack, setPack] = useState('classic');
    const [style, setStyle] = useState('solid');

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const root = document.documentElement;
        const read = () => {
            const nextPack = root.dataset.faPackOverride || 'classic';
            const nextStyle =
                root.dataset.faStyleOverride &&
                ['solid', 'regular', 'light', 'thin'].includes(
                    root.dataset.faStyleOverride
                )
                    ? root.dataset.faStyleOverride
                    : 'solid';
            setPack(nextPack);
            setStyle(nextStyle);
        };
        read();
        const observer = new MutationObserver(() => read());
        observer.observe(root, {
            attributes: true,
            attributeFilter: [
                'data-fa-pack-override',
                'data-fa-style-override',
            ],
        });
        return () => observer.disconnect();
    }, []);

    return { pack, style };
};

function ReviewBody({
    body,
    isBento,
    clampValue = 3,
    minSize = 8,
    maxSize = 18,
    iconColor = '',
    starColor = '',
    iconClassName = '',
    featureIcon = 'quote',
    featureIconPack = 'classic',
    featureIconStyle = 'solid',
}) {
    const containerRef = useRef(null);
    const textRef = useRef(null);
    const resolvedMinSize = Number(minSize) || 8;
    const resolvedMaxSize = Number(maxSize) || 18;
    const [fitSize, setFitSize] = useState(resolvedMaxSize);

    useEffect(() => {
        if (!isBento) return;
        const container = containerRef.current;
        const textEl = textRef.current;
        if (!container || !textEl) return;

        let frameId;

        const computeSize = () => {
            const { clientWidth: w = 0, clientHeight: h = 0 } = container;
            if (!w || !h) return;

            let size = resolvedMaxSize;
            const maxIterations = 90;
            let iterations = 0;
            const minFloor = 6;
            const minAllowed = Math.min(resolvedMinSize, minFloor);
            const fits = () =>
                textEl.scrollHeight <= h && textEl.scrollWidth <= w;

            textEl.style.fontSize = `${size}px`;
            textEl.style.lineHeight = '1.35';

            while (!fits() && size > minAllowed && iterations < maxIterations) {
                size = Math.max(minAllowed, size - 1);
                textEl.style.fontSize = `${size}px`;
                iterations += 1;
            }

            setFitSize((prev) => (prev === size ? prev : size));
        };

        const scheduleCompute = () => {
            cancelAnimationFrame(frameId);
            frameId = requestAnimationFrame(computeSize);
        };

        scheduleCompute();
        const ro = new ResizeObserver(scheduleCompute);
        ro.observe(container);
        window.addEventListener('resize', scheduleCompute);
        return () => {
            cancelAnimationFrame(frameId);
            ro.disconnect();
            window.removeEventListener('resize', scheduleCompute);
        };
    }, [body, isBento, resolvedMaxSize, resolvedMinSize]);

    if (isBento) {
        return (
            <div
                className="flex h-full min-h-0 w-full flex-1 overflow-hidden leading-6"
                ref={containerRef}
            >
                <div
                    ref={textRef}
                    className="h-full w-full hyphens-none whitespace-normal break-normal"
                    style={{
                        fontSize: `${fitSize}px`,
                        lineHeight: '1.35',
                        wordBreak: 'normal',
                        overflowWrap: 'normal',
                    }}
                >
                    <div
                        className={`flex flex-col ${iconClassName}`}
                        style={{ fontSize: `${fitSize}px` }}
                    >
                        {featureIcon === 'stars' ? (
                            <div
                                className="flex items-center leading-none"
                                style={{
                                    height: '1em',
                                    minHeight: '1em',
                                    gap: '4px',
                                }}
                            >
                                {[...Array(5)].map((_, i) => (
                                    <i
                                        key={i}
                                        className={`${buildIconClass(
                                            'star',
                                            featureIconPack,
                                            featureIconStyle
                                        )} ${starColor}`}
                                        aria-hidden="true"
                                        style={{
                                            lineHeight: 1,
                                            fontSize: '0.75em',
                                        }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <i
                                className={`${buildIconClass(
                                    'quote-left',
                                    featureIconPack,
                                    featureIconStyle
                                )} ${iconColor}`}
                                aria-hidden="true"
                                style={{ lineHeight: 1 }}
                            />
                        )}
                    </div>
                    <div className="mt-2">
                        <MarkDown
                            className="w-full"
                            components={{
                                p: ({ children }) => (
                                    <p
                                        className="m-0 whitespace-normal break-normal"
                                        style={{
                                            wordBreak: 'normal',
                                            overflowWrap: 'normal',
                                        }}
                                    >
                                        {children}
                                    </p>
                                ),
                                strong: ({ children }) => (
                                    <strong className="font-semibold">
                                        {children}
                                    </strong>
                                ),
                                em: ({ children }) => (
                                    <em className="italic">{children}</em>
                                ),
                            }}
                        >
                            {body}
                        </MarkDown>
                    </div>
                </div>
            </div>
        );
    }

    const bodyClampStyle = clampValue
        ? {
              display: '-webkit-box',
              WebkitLineClamp: clampValue,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
          }
        : undefined;

    return (
        <div className="flex min-h-0 w-full flex-1 overflow-hidden leading-6">
            <div className="flex w-full flex-col">
                <div className={`flex flex-col ${iconClassName}`}>
                    {featureIcon === 'stars' ? (
                        <div
                            className="flex items-center leading-none"
                            style={{
                                height: '1em',
                                minHeight: '1em',
                                gap: '4px',
                            }}
                        >
                            {[...Array(5)].map((_, i) => (
                                <i
                                    key={i}
                                    className={`${buildIconClass(
                                        'star',
                                        featureIconPack,
                                        featureIconStyle
                                    )} ${starColor}`}
                                    aria-hidden="true"
                                    style={{
                                        lineHeight: 1,
                                        fontSize: '0.75em',
                                    }}
                                />
                            ))}
                        </div>
                    ) : (
                        <i
                            className={`${buildIconClass(
                                'quote-left',
                                featureIconPack,
                                featureIconStyle
                            )} ${iconColor}`}
                            aria-hidden="true"
                            style={{ lineHeight: 1 }}
                        />
                    )}
                </div>
                <div className="mt-2">
                    <MarkDown
                        className="w-full"
                        components={{
                            p: ({ children }) => (
                                <p
                                    className="m-0 whitespace-normal break-words"
                                    style={bodyClampStyle}
                                >
                                    {children}
                                </p>
                            ),
                        }}
                    >
                        {body}
                    </MarkDown>
                </div>
            </div>
        </div>
    );
}

export default function Reviews(props) {
    const {
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        OutlineButtonOpenInNewTab,
        Tagline,
        Header,
        Reviews,
        FeatureIcon = 'quote',
        FeatureIconPack = '',
        FeatureIconStyle = '',
        FeatureIconSize = 'default',
        ButtonLink,
        ButtonText,
        ButtonOpenInNewTab,
        ReviewSource = 'manual',
        NiceJobLayout = 'grid',
        NiceJobFilters = [],
        Layout = 'slider',
        LayoutVariant, // backward compatibility
        ColorScheme = 'light',
        BackgroundImage,
        BackgroundImageUrl,
        BackgroundImagePosition = '50',
        BackgroundFixed = false,
        VerticalSpacing = 'medium',
        Animation = 'none',
        AnimationGroup = 'features',
        Duration = 0.6,
        EditorPreview = false,
    } = props;

    const [limitOneTestimonial, setLimitOneTestimonial] = useState(true);
    const [prefersReduced, setPrefersReduced] = useState(false);
    const { pack: sitePack, style: siteStyle } = useSiteIconOverride();
    const effectiveIconPack =
        FeatureIconPack && FeatureIconPack.trim().length
            ? FeatureIconPack.trim()
            : sitePack;
    const effectiveIconStyle =
        FeatureIconStyle && FeatureIconStyle.trim().length
            ? FeatureIconStyle.trim()
            : siteStyle;
    const featureIconSizeClass = getFeatureIconSizeClass(FeatureIconSize);
    const squareCardSizeClass = getSquareCardSizeClass(FeatureIconSize);
    const useNiceJobReviews =
        String(ReviewSource).toLowerCase().trim() === 'nicejob';
    const rawLayoutVariant = String(Layout || LayoutVariant || 'grid')
        .toLowerCase()
        .trim();
    const layoutVariant =
        rawLayoutVariant === 'slider-full' ? 'slider' : rawLayoutVariant;
    const backgroundImage = (
        typeof BackgroundImage === 'string' ? BackgroundImage : ''
    ).trim();
    const backgroundImageUrl = (
        typeof BackgroundImageUrl === 'string' ? BackgroundImageUrl : ''
    ).trim();
    const resolvedBackgroundImage = backgroundImage || backgroundImageUrl;
    const { x: bgPosX, y: bgPosY } = parseImagePosition(
        BackgroundImagePosition
    );
    const hasBackgroundImage = Boolean(resolvedBackgroundImage);
    const shouldFixBackground =
        typeof BackgroundFixed === 'string'
            ? BackgroundFixed.toLowerCase() === 'true'
            : !!BackgroundFixed;

    const preset = normalizeAnimation(Animation);
    const animationGroup = normalizeAnimationGroup(AnimationGroup);
    const duration = Math.max(0.2, Number(Duration) || 0.6);
    const enableAnim = !prefersReduced && preset !== 'none';
    const isStagger = preset === 'stagger';
    const viewport = { once: !EditorPreview, amount: 0.5 };
    const baseKey = `reviews-${animationGroup}-${preset}-${duration}-${
        EditorPreview ? 'preview' : 'live'
    }`;
    const animateContent = animationGroup === 'content';
    const animateCards =
        animationGroup === 'features' || animationGroup === 'content';
    const animateCTAs = animationGroup === 'ctas';

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = (event) => setPrefersReduced(event.matches);
        setPrefersReduced(mq.matches);
        mq.addEventListener?.('change', update);
        return () => mq.removeEventListener?.('change', update);
    }, []);

    const childMotionProps = useMemo(() => {
        if (!enableAnim) return {};
        const variants = getVariantFromSimpleControls(
            isStagger ? 'fade' : preset,
            duration
        );
        const hasHiddenShow =
            variants &&
            (Object.prototype.hasOwnProperty.call(variants, 'hidden') ||
                Object.prototype.hasOwnProperty.call(variants, 'show'));
        if (EditorPreview) {
            return {
                variants,
                initial: hasHiddenShow ? 'hidden' : 'offscreen',
                animate: hasHiddenShow ? 'show' : 'onscreen',
            };
        }
        return {
            variants,
            initial: hasHiddenShow ? 'hidden' : 'offscreen',
            whileInView: hasHiddenShow ? 'show' : 'onscreen',
            viewport,
        };
    }, [enableAnim, isStagger, preset, duration, EditorPreview]);

    const containerMotionProps = useMemo(() => {
        if (!enableAnim || !isStagger) return {};
        const variants = getVariantFromSimpleControls('stagger', duration);
        if (EditorPreview) {
            return { variants, initial: 'hidden', animate: 'show' };
        }
        return { variants, initial: 'hidden', whileInView: 'show', viewport };
    }, [enableAnim, isStagger, duration, EditorPreview]);

    const staggerCardVariants = useMemo(
        () => ({
            hidden: { opacity: 0, y: 16 },
            show: {
                opacity: 1,
                y: 0,
                transition: { duration: Math.max(0.3, duration - 0.2) },
            },
        }),
        [duration]
    );

    useEffect(() => {
        if (window.innerWidth >= 1024) {
            setLimitOneTestimonial(false);
        }
    }, []);

    const fullColorScheme = getColorScheme(
        ColorScheme,
        props.GradientDirection
    );
    const {
        backgroundColor,
        headerColor,
        iconColor,
        secondaryButton,
        thirdButton,
        boxTextColor,
        textColor,
        taglineColor,
        boxBorder,
        overlayColor,
        bgOverlay,
    } = fullColorScheme;

    const boxColor = fullColorScheme.boxColor?.trim()
        ? fullColorScheme.boxColor
        : backgroundColor === 'bg-white' || backgroundColor === 'bg-neutral-100'
        ? 'bg-neutral-100'
        : bgOverlay?.trim()
        ? bgOverlay
        : 'bg-white/20';

    const resolvedBoxTextColor =
        boxTextColor?.trim() || textColor?.trim() || 'text-neutral-800';
    const starColor = iconColor || 'text-primary-700';

    const bentoGridRef = useRef(null);
    const [bentoUnits, setBentoUnits] = useState({
        unitWidth: 0,
        unitHeight: 0,
        columnCount: 3,
    });
    const [bentoRowSize, setBentoRowSize] = useState(null);
    const [slidesToShow, setSlidesToShow] = useState(1);

    const bentoSpans = (index) => {
        const spans = [
            'col-span-2 row-span-1 sm:col-span-2 sm:row-span-1 md:col-span-2 md:row-span-1 lg:col-span-2 lg:row-span-1',
            'col-span-1 row-span-1 sm:col-span-1 sm:row-span-1',
            'col-span-1 row-span-2 sm:col-span-1 sm:row-span-2',
            'col-span-1 row-span-2 sm:col-span-1 sm:row-span-2',
            'col-span-1 row-span-1 sm:col-span-1 sm:row-span-1',
            'col-span-2 row-span-3 sm:col-span-3 sm:row-span-2 md:col-span-3 md:row-span-2 lg:col-span-3 lg:row-span-2',
            'col-span-1 row-span-1 sm:col-span-1 sm:row-span-1',
            'col-span-1 row-span-1 sm:col-span-1 sm:row-span-1',
            'col-span-2 row-span-1 sm:col-span-2 sm:row-span-1',
        ];
        return spans[index] || 'col-span-2';
    };

    let cardsWrapClass =
        'flex flex-wrap items-stretch justify-center gap-4 lg:gap-6';
    let cardItemClass =
        'flex h-full w-full max-w-md flex-1 shrink-0 basis-full justify-center sm:basis-[calc(50%-0.75rem)] lg:basis-[calc(33.333%-1rem)]';
    let spanForIndex = () => '';
    let figureHeightClass = '';
    let figureBaseHeight = 'h-full';

    const getColumnCountFromStyle = (style) => {
        const template = style?.gridTemplateColumns || '';
        const repeatMatch = template.match(/repeat\((\d+)/);
        const repeatCount = repeatMatch ? Number(repeatMatch[1]) : 0;
        if (Number.isFinite(repeatCount) && repeatCount > 0) return repeatCount;

        const tokens = template.split(' ').filter(Boolean);
        if (tokens.length > 0) return tokens.length;

        if (typeof window !== 'undefined' && window.innerWidth >= 640) return 6;
        return 3;
    };

    const extractSpanValue = (spanClass, type, useSm) => {
        const baseRegex = new RegExp(`(?:^|\\s)${type}-span-(\\d+)`);
        const smRegex = new RegExp(`sm:${type}-span-(\\d+)`);
        const preferred = (useSm ? smRegex : baseRegex).exec(spanClass);
        if (preferred?.[1]) return Number(preferred[1]) || 1;
        const fallback = (useSm ? baseRegex : smRegex).exec(spanClass);
        return fallback?.[1] ? Number(fallback[1]) || 1 : 1;
    };

    useEffect(() => {
        if (layoutVariant !== 'bento') return;
        const gridEl = bentoGridRef.current;
        if (!gridEl) return;

        const computeUnits = () => {
            const style = window.getComputedStyle(gridEl);
            const columnCount = getColumnCountFromStyle(style);
            const gapX = parseFloat(style.columnGap || style.gap || '0') || 0;
            const effectiveWidth =
                gridEl.clientWidth - gapX * Math.max(0, (columnCount || 1) - 1);
            const unitWidth = effectiveWidth / Math.max(1, columnCount || 1);

            const autoRowsValue = style.gridAutoRows;
            let unitHeight = parseFloat(autoRowsValue);
            const needsMeasuredHeight =
                !Number.isFinite(unitHeight) ||
                unitHeight <= 0 ||
                autoRowsValue.includes('clamp(');

            if (needsMeasuredHeight && gridEl.firstElementChild) {
                const firstSpanClass = gridEl.firstElementChild.className || '';
                const firstRowSpan = extractSpanValue(
                    firstSpanClass,
                    'row',
                    columnCount >= 6
                );
                const measuredHeight =
                    gridEl.firstElementChild.getBoundingClientRect().height;
                if (measuredHeight) {
                    unitHeight =
                        measuredHeight / Math.max(1, firstRowSpan || 1);
                }
            }

            if (!Number.isFinite(unitHeight) || unitHeight <= 0) {
                const rows = Math.max(
                    1,
                    Math.ceil((Reviews?.length || 1) / columnCount || 1)
                );
                unitHeight = gridEl.clientHeight / rows || 0;
            }

            if (unitWidth > 0) {
                const squareRowSize = unitWidth;
                setBentoRowSize((prev) =>
                    prev === squareRowSize ? prev : squareRowSize
                );
                unitHeight = squareRowSize;
            }

            setBentoUnits((prev) => {
                if (
                    prev.unitWidth === unitWidth &&
                    prev.unitHeight === unitHeight &&
                    prev.columnCount === columnCount
                ) {
                    return prev;
                }
                return { unitWidth, unitHeight, columnCount };
            });
        };

        computeUnits();
        const ro = new ResizeObserver(computeUnits);
        ro.observe(gridEl);
        window.addEventListener('resize', computeUnits);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', computeUnits);
        };
    }, [layoutVariant, Reviews?.length]);

    useEffect(() => {
        if (layoutVariant !== 'slider') return;
        const updateSlides = () => {
            if (window.innerWidth < 640) {
                setSlidesToShow(1);
            } else if (window.innerWidth < 1024) {
                setSlidesToShow(2);
            } else {
                setSlidesToShow(3);
            }
        };
        updateSlides();
        window.addEventListener('resize', updateSlides);
        return () => window.removeEventListener('resize', updateSlides);
    }, [layoutVariant]);

    if (!useNiceJobReviews && !Reviews) {
        return <div>failed to load</div>;
    }

    const getBentoCardSize = (spanClass) => {
        const { unitWidth, unitHeight, columnCount } = bentoUnits;
        if (!unitWidth || !unitHeight) return null;
        const useSmVariant = columnCount >= 6;
        const colSpan = extractSpanValue(spanClass, 'col', useSmVariant);
        const rowSpan = extractSpanValue(spanClass, 'row', useSmVariant);
        return {
            width: unitWidth * colSpan,
            height: unitHeight * rowSpan,
            colSpan,
            rowSpan,
        };
    };

    switch (layoutVariant) {
        case 'bento':
            cardsWrapClass =
                'grid grid-flow-dense auto-rows-[clamp(7rem,_16vw,_9rem)] sm:auto-rows-[clamp(7rem,_16vw,_9rem)] md:auto-rows-[clamp(7rem,_16vw,_9rem)] lg:auto-rows-[clamp(9rem,_8vw,_12rem)] gap-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-6';
            cardItemClass = 'flex h-full w-full min-h-0';
            figureHeightClass = 'h-full min-h-0';
            spanForIndex = bentoSpans;
            break;
        case 'masonry':
            cardsWrapClass = 'columns-1 sm:columns-2 lg:columns-3 gap-4';
            cardItemClass = 'mb-4 flex h-full break-inside-avoid';
            break;
        case 'square':
            cardsWrapClass =
                'flex flex-wrap items-start justify-center gap-4 lg:gap-6';
            cardItemClass = `flex ${squareCardSizeClass} shrink-0 justify-center`;
            figureHeightClass = 'h-full';
            figureBaseHeight = 'h-full';
            break;
        case 'slider':
            cardsWrapClass = 'w-full';
            cardItemClass = 'flex h-full w-full';
            break;
        default:
            figureHeightClass = 'min-h-[240px]';
            break;
    }

    const vSpace = getVerticalSpacing(VerticalSpacing);

    const hasNewOutlineCTA = hasText(OutlineButtonText);
    const resolvedOutlineText = hasNewOutlineCTA
        ? OutlineButtonText
        : ButtonText;
    const resolvedOutlineLink = hasNewOutlineCTA
        ? OutlineButtonLink
        : ButtonLink;
    const resolvedOutlineOpenInNewTab = hasNewOutlineCTA
        ? OutlineButtonOpenInNewTab
        : ButtonOpenInNewTab;

    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText: resolvedOutlineText,
            OutlineButtonLink: resolvedOutlineLink,
            OutlineButtonOpenInNewTab: resolvedOutlineOpenInNewTab,
        },
        {
            primaryVariant: 'primary',
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: 'justify-center',
            wrapSpacing: 'mt-10 lg:mt-16',
            className: 'items-center',
            buttonsClassName:
                'flex flex-wrap items-center justify-center gap-4',
            enableAnim: false,
        }
    );

    const squareClass = layoutVariant === 'square' ? 'aspect-square' : '';
    const hasTagline = hasText(Tagline);
    const hasHeader = hasText(Header);
    const showHeading = hasTagline || hasHeader;

    return (
        <div
            className={`editable-component relative overflow-hidden ${backgroundColor} ${vSpace}`}
            style={fullColorScheme.backgroundStyle}
            data-json="testimonials"
        >
            {hasBackgroundImage && (
                <div className="absolute inset-0 z-0">
                    <div
                        className={`absolute inset-0 h-full w-full bg-cover bg-no-repeat ${
                            shouldFixBackground ? 'bg-scroll lg:bg-fixed' : ''
                        }`}
                        style={{
                            backgroundImage: `url(${resolvedBackgroundImage})`,
                            backgroundPosition: `${bgPosX}% ${bgPosY}%`,
                        }}
                    />
                    <div
                        className={`absolute inset-0 ${overlayColor || ''}`}
                        style={fullColorScheme.overlayStyle}
                    />
                </div>
            )}
            <div className="relative z-40 mx-auto max-w-7xl px-6 lg:px-8">
                {showHeading && (
                    <MotionMaybe
                        enable={enableAnim && animateContent}
                        nodeKey={`${baseKey}-content`}
                        motionProps={childMotionProps}
                        className="mx-auto max-w-2xl text-center"
                    >
                        {hasTagline && (
                            <MarkDown
                                variant="h2"
                                className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                            >
                                {Tagline}
                            </MarkDown>
                        )}
                        {hasHeader && (
                            <MarkDown
                                variant="h2"
                                className={`${
                                    hasTagline ? 'mt-2' : ''
                                } font-display text-3xl tracking-tight ${headerColor} sm:text-4xl sm:leading-tight`}
                            >
                                {Header}
                            </MarkDown>
                        )}
                    </MotionMaybe>
                )}

                <div
                    className={`mx-auto flow-root max-w-3xl lg:mx-0 lg:max-w-none ${
                        showHeading ? 'mt-10' : ''
                    }`}
                >
                    <MotionMaybe
                        enable={enableAnim && animateCards && isStagger}
                        nodeKey={`${baseKey}-cards-container`}
                        motionProps={containerMotionProps}
                        className="relative"
                    >
                        {useNiceJobReviews ? (
                            <NiceJobStories
                                layout={NiceJobLayout}
                                filters={NiceJobFilters}
                                className={
                                    String(NiceJobLayout)
                                        .toLowerCase()
                                        .trim() === 'single'
                                        ? 'mx-auto max-w-3xl'
                                        : 'w-full'
                                }
                            />
                        ) : layoutVariant === 'slider' ? (
                            <Carousel
                                renderCenterLeftControls={({
                                    previousSlide,
                                    previousDisabled,
                                }) => (
                                    <button
                                        className="fa-solid fa-angle-left relative -translate-x-4 text-3xl text-neutral-800 opacity-0 transition-opacity duration-500 ease-in-out hover:opacity-100 md:opacity-20"
                                        onClick={previousSlide}
                                        disabled={previousDisabled}
                                        aria-label="Previous"
                                    ></button>
                                )}
                                renderCenterRightControls={({
                                    nextSlide,
                                    nextDisabled,
                                }) => (
                                    <button
                                        className="fa-solid fa-angle-right relative translate-x-4 text-3xl text-neutral-800 opacity-0 transition-opacity duration-500 ease-in-out hover:opacity-100 md:opacity-20"
                                        onClick={nextSlide}
                                        disabled={nextDisabled}
                                        aria-label="Next"
                                    ></button>
                                )}
                                renderBottomCenterControls={() => <div></div>}
                                cellAlign="center"
                                cellSpacing={16}
                                className="mx-auto w-full max-w-5xl"
                                disableEdgeSwiping
                                withoutControls={false}
                                pauseOnHover
                                autoplay
                                autoplayInterval={5000}
                                wrapAround
                                slidesToShow={slidesToShow}
                            >
                                {Reviews.slice(
                                    0,
                                    limitOneTestimonial ? 1 : Reviews.length
                                ).map((Review, index) => {
                                    const isBento = false;
                                    const minSize = undefined;
                                    const maxSize = undefined;
                                    const clampValue = 5;
                                    const figurePaddingClass = 'p-8';
                                    const footerMarginClass = 'mt-5';
                                    const quoteIconClass =
                                        featureIconSizeClass ||
                                        'text-xl sm:text-2xl';

                                    return (
                                        <MotionMaybe
                                            key={`${baseKey}-slider-card-${index}`}
                                            enable={enableAnim && animateCards}
                                            nodeKey={`${baseKey}-slider-card-${index}`}
                                            motionProps={
                                                isStagger
                                                    ? {
                                                          variants:
                                                              staggerCardVariants,
                                                      }
                                                    : childMotionProps
                                            }
                                            className="h-full w-full p-2"
                                        >
                                            <figure
                                                className={`${boxColor} ${boxBorder} relative flex h-full w-full flex-col rounded-[min(1.5rem,var(--shape-radius))] ${resolvedBoxTextColor} ${figurePaddingClass} text-sm leading-6`}
                                            >
                                                <div className="min-h-0 grow">
                                                    <blockquote className="flex h-full min-h-0 flex-1 flex-col">
                                                        <ReviewBody
                                                            body={Review.Body}
                                                            isBento={isBento}
                                                            clampValue={
                                                                clampValue
                                                            }
                                                            minSize={minSize}
                                                            maxSize={maxSize}
                                                            iconColor={
                                                                iconColor
                                                            }
                                                            starColor={
                                                                starColor
                                                            }
                                                            iconClassName={
                                                                quoteIconClass
                                                            }
                                                            featureIcon={
                                                                FeatureIcon
                                                            }
                                                            featureIconPack={
                                                                effectiveIconPack
                                                            }
                                                            featureIconStyle={
                                                                effectiveIconStyle
                                                            }
                                                        />
                                                    </blockquote>
                                                </div>
                                                <figcaption
                                                    className={`flex items-end ${footerMarginClass}`}
                                                >
                                                    <img
                                                        className="h-6 w-6 rounded-full bg-neutral-50 ring-2 ring-white"
                                                        src={normalizeReviewSourceIcon(
                                                            Review.Author.Icon
                                                        )}
                                                        alt={getReviewSourceAlt(
                                                            Review.Author.Icon
                                                        )}
                                                    />
                                                    <MarkDown
                                                        className={`ml-3 font-semibold ${resolvedBoxTextColor}`}
                                                        components={{
                                                            p: ({
                                                                children,
                                                            }) => (
                                                                <p className="m-0 whitespace-pre-wrap">
                                                                    {children}
                                                                </p>
                                                            ),
                                                        }}
                                                    >
                                                        {Review.Author.Name}
                                                    </MarkDown>
                                                </figcaption>
                                            </figure>
                                        </MotionMaybe>
                                    );
                                })}
                            </Carousel>
                        ) : (
                            <div
                                className={cardsWrapClass}
                                ref={
                                    layoutVariant === 'bento'
                                        ? bentoGridRef
                                        : null
                                }
                                style={
                                    layoutVariant === 'bento' && bentoRowSize
                                        ? { gridAutoRows: `${bentoRowSize}px` }
                                        : undefined
                                }
                            >
                                {Reviews.slice(
                                    0,
                                    limitOneTestimonial ? 1 : Reviews.length
                                ).map((Review, index) => {
                                    const isBento = layoutVariant === 'bento';
                                    const spanClass = spanForIndex(index);
                                    const cardSize = isBento
                                        ? getBentoCardSize(spanClass)
                                        : null;
                                    const isLargeBento =
                                        isBento &&
                                        (spanClass.includes('row-span-2') ||
                                            spanClass.includes('col-span-2') ||
                                            spanClass.includes('col-span-3'));
                                    const minSize = isBento
                                        ? isLargeBento
                                            ? 9
                                            : 6
                                        : undefined;
                                    const maxSize = isBento
                                        ? isLargeBento
                                            ? 56
                                            : 18
                                        : undefined;
                                    const clampValue =
                                        isBento || layoutVariant === 'masonry'
                                            ? null
                                            : layoutVariant === 'grid'
                                            ? 4
                                            : layoutVariant === 'square'
                                            ? 5
                                            : 3;
                                    const figurePaddingClass = isBento
                                        ? 'p-6'
                                        : 'p-8';
                                    const footerMarginClass = isBento
                                        ? 'mt-6'
                                        : 'mt-5';
                                    const quoteIconClass = isBento
                                        ? featureIconSizeClass
                                        : 'text-xl sm:text-2xl';
                                    const resolvedQuoteIconClass =
                                        featureIconSizeClass || quoteIconClass;

                                    return (
                                        <MotionMaybe
                                            key={`${baseKey}-card-${index}`}
                                            enable={enableAnim && animateCards}
                                            nodeKey={`${baseKey}-card-${index}`}
                                            motionProps={
                                                isStagger
                                                    ? {
                                                          variants:
                                                              staggerCardVariants,
                                                      }
                                                    : childMotionProps
                                            }
                                            className={`${cardItemClass} ${spanForIndex(
                                                index
                                            )}`}
                                        >
                                            <figure
                                                className={`${boxColor} ${boxBorder} relative flex ${figureBaseHeight} w-full flex-col rounded-[min(1.5rem,var(--shape-radius))] ${resolvedBoxTextColor} ${figurePaddingClass} text-sm leading-6 ${squareClass} ${figureHeightClass} ${
                                                    isBento
                                                        ? 'overflow-hidden'
                                                        : ''
                                                }`}
                                                style={
                                                    isBento && cardSize
                                                        ? {
                                                              '--card-width': `${cardSize.width}px`,
                                                              '--card-height': `${cardSize.height}px`,
                                                          }
                                                        : undefined
                                                }
                                                data-card-width={
                                                    cardSize
                                                        ? Math.round(
                                                              cardSize.width
                                                          )
                                                        : undefined
                                                }
                                                data-card-height={
                                                    cardSize
                                                        ? Math.round(
                                                              cardSize.height
                                                          )
                                                        : undefined
                                                }
                                                data-col-span={
                                                    cardSize?.colSpan ||
                                                    undefined
                                                }
                                                data-row-span={
                                                    cardSize?.rowSpan ||
                                                    undefined
                                                }
                                            >
                                                <div className="min-h-0 grow">
                                                    <blockquote className="flex h-full min-h-0 flex-1 flex-col">
                                                        <ReviewBody
                                                            body={Review.Body}
                                                            isBento={isBento}
                                                            clampValue={
                                                                clampValue
                                                            }
                                                            minSize={minSize}
                                                            maxSize={maxSize}
                                                            iconColor={
                                                                iconColor
                                                            }
                                                            starColor={
                                                                starColor
                                                            }
                                                            iconClassName={
                                                                resolvedQuoteIconClass
                                                            }
                                                            featureIcon={
                                                                FeatureIcon
                                                            }
                                                            featureIconPack={
                                                                effectiveIconPack
                                                            }
                                                            featureIconStyle={
                                                                effectiveIconStyle
                                                            }
                                                        />
                                                    </blockquote>
                                                </div>
                                                <figcaption
                                                    className={`flex items-end ${footerMarginClass}`}
                                                >
                                                    <img
                                                        className={`rounded-full bg-neutral-50 ring-2 ring-white ${
                                                            isBento
                                                                ? 'h-5 w-5'
                                                                : 'h-6 w-6'
                                                        }`}
                                                        src={normalizeReviewSourceIcon(
                                                            Review.Author.Icon
                                                        )}
                                                        alt={getReviewSourceAlt(
                                                            Review.Author.Icon
                                                        )}
                                                    />
                                                    <MarkDown
                                                        className={`ml-3 font-semibold ${resolvedBoxTextColor} ${
                                                            isBento
                                                                ? 'text-xs'
                                                                : ''
                                                        }`}
                                                        components={{
                                                            p: ({
                                                                children,
                                                            }) => (
                                                                <p className="m-0 whitespace-pre-wrap">
                                                                    {children}
                                                                </p>
                                                            ),
                                                        }}
                                                    >
                                                        {Review.Author.Name}
                                                    </MarkDown>
                                                </figcaption>
                                            </figure>
                                        </MotionMaybe>
                                    );
                                })}
                            </div>
                        )}
                    </MotionMaybe>

                    {hasCTAs && (
                        <MotionMaybe
                            enable={enableAnim && animateCTAs}
                            nodeKey={`${baseKey}-ctas`}
                            motionProps={childMotionProps}
                        >
                            {CTAs}
                        </MotionMaybe>
                    )}
                </div>
            </div>
        </div>
    );
}
